/**
 * Analytics API
 *
 * In development:  requests go to /ngrok-proxy/api/analytics.php/...
 *                  CRA dev server (setupProxy.js) forwards them to the ngrok
 *                  backend server-to-server — no CORS preflight in the browser.
 *
 * In production:   set REACT_APP_ANALYTICS_BASE_URL to the full backend URL.
 */

import { getAuthToken } from '../utils/authToken';

const ANALYTICS_BASE =
  process.env.REACT_APP_ANALYTICS_BASE_URL ||
  '/ngrok-proxy/api/analytics.php';

const REPORT_TYPES = {
  SCANS_OVER_TIME: 'scansOverTime',
  TOP_QR_CODES: 'topQrCodes',
  DEVICE_BROWSER: 'deviceBrowserBreakdown',
  QR_CREATORS: 'qrCodeCreators',
};

/**
 * Fetches a single report from the analytics API.
 * Throws on network failure or non-success API response.
 *
 * @param {string} reportType - One of the REPORT_TYPES values
 * @param {string} range      - Either a preset key (e.g. 'last_30_days') or
 *                              'custom:YYYY-MM-DD:YYYY-MM-DD' for a date range
 * @returns {Promise<{ data: any }>}
 */
export async function fetchReport(reportType, range) {
  // Custom ranges arrive as 'custom:2026-03-11:2026-04-30'
  // The server expects ?start=2026-03-11&end=2026-04-30 instead of ?range=...
  let queryString;
  if (range && range.startsWith('custom:')) {
    const [, start, end] = range.split(':');
    queryString = `start=${start}&end=${end}`;
  } else {
    queryString = `range=${range}`;
  }

  const url = `${ANALYTICS_BASE}/${reportType}?${queryString}`;
  const token = getAuthToken();

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const json = await res.json();

  if (json.status !== 'success') throw new Error(json.message || 'API error');

  return { data: json.data };
}

export { REPORT_TYPES };
